import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useState } from "react";
import axios from "axios";

// reducer - state management
import { LOGIN, LOGOUT } from "store/actions";
import accountReducer from "store/accountReducer";

// project imports
import Loader from "ui-component/Loader";
import { useDispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

// Create direct axios instance for backend calls
const backendAPI = axios.create({
  baseURL: "https://api.moofunk169.com/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const [isInitialized, setIsInitialized] = useState(false);
  const reduxDispatch = useDispatch();

  // Handle force logout
  const handleForceLogout = () => {
    localStorage.removeItem("serviceToken");

    dispatch({ type: LOGOUT });

    // Show notification
    reduxDispatch(
      openSnackbar({
        open: true,
        message: "คุณถูกออกจากระบบเนื่องจากมีการเข้าสู่ระบบจากอุปกรณ์อื่น",
        variant: "alert",
        alert: {
          color: "warning",
        },
        close: true,
      })
    );

    // Clear local storage and redirect
    window.localStorage.clear();
    window.location.href = "/login";
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Check if admin has a valid token stored
        const token = localStorage.getItem("serviceToken");
        if (token) {
          try {
            // Call backend API for admin authentication directly
            const response = await backendAPI.get("/api/admin/account/me", {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.data && response.data.user) {
              const user = response.data.user;

              dispatch({
                type: LOGIN,
                payload: {
                  isLoggedIn: true,
                  user: {
                    id: user.id,
                    username: user.username,
                    name: user.name,
                    role: user.role,
                  },
                },
              });
            } else {
              throw new Error("Invalid token");
            }
          } catch (err) {
            console.error("Token verification error:", err);
            // Check if it's a session invalidation
            if (err.response?.data?.code === "SESSION_INVALIDATED") {
              console.log("Admin session invalidated - logged in elsewhere");
              reduxDispatch(
                openSnackbar({
                  open: true,
                  message: "Session expired - logged in elsewhere",
                  variant: "alert",
                  alert: {
                    color: "warning",
                  },
                  close: true,
                })
              );
            }

            // Token is invalid, remove it
            localStorage.removeItem("serviceToken");
            dispatch({
              type: LOGOUT,
            });
          }
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error("Admin token verification failed:", err);
        localStorage.removeItem("serviceToken");
        dispatch({
          type: LOGOUT,
        });
      } finally {
        setIsInitialized(true);
      }
    };

    init();
  }, [reduxDispatch]);

  const login = async (username, password) => {
    try {
      // Call backend admin login API directly
      const response = await backendAPI.post("/api/admin/account/login", {
        username,
        password,
      });

      const { serviceToken, user } = response.data;

      if (!serviceToken || !user) {
        throw new Error("Invalid response from server");
      }

      // Store the token
      localStorage.setItem("serviceToken", serviceToken);

      // Dispatch login action
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: {
            id: user.id,
            username: user.username,
            name: user.name,
            role: user.role,
          },
        },
      });

      return response.data;
    } catch (error) {
      console.error("Login failed:", error);

      // Clear any stored token on login failure
      localStorage.removeItem("serviceToken");

      throw error;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("serviceToken");
      if (token) {
        await backendAPI.post(
          "/api/admin/account/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("serviceToken");
      dispatch({ type: LOGOUT });
      window.localStorage.clear();
      window.location.href = "/login";
    }
  };

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
